<template>
  <b-container fluid class="p-0 fullContent">
    <b-row>
      <b-col class="px-3 pb-5 px-6-md pb-2-md shadow bg-white" offset-xl="1" xl="10">
        <h2 class="pt-3 px-2 px-lg-0">User Management</h2>
        <LoadingSpinner v-if="loading" :message="`Loading Users`" />
        <div v-if="!isExtraSmall" class="d-flex pt-3 pb-3 px-2 px-lg-0">
          <button v-for="roleSwitch in roleSwitches"
            :key="roleSwitch.id"
            class="underline-button"
            :class="{'active': activeType === roleSwitch.id}"
            @click="roleSwitched(roleSwitch)"
          >{{roleSwitch.label}}</button>
          <div v-if="['MFS', 'TCEO', 'INFWB'].includes(activeType)" class="dock-right">
            <b-button
              variant="secondary"
              pill
              :to="{name: 'createUser', params: { type: activeType }}"
              aria-label="Add new user"
            >{{`Add ${activeType} User`}}</b-button>
          </div>
        </div>
        <div v-else class="px-2 py-2">
          <select
            class="form-control mb-2"
            @change="roleSwitchSelected($event.target.value)"
          >
              <option v-for="roleSwitch in roleSwitches" :key="roleSwitch.id" :value="roleSwitch.id">{{roleSwitch.label}}</option>
          </select>
          <div v-if="['MFS', 'TCEO', 'INFWB'].includes(activeType)">
            <b-button
              class="w-100"
              variant="secondary"
              pill
              :to="{name: 'createUser', params: { type: activeType }}"
            >{{`Add User`}}</b-button>
          </div>
        </div>
        <UserFilter
          class="w-lg-50 px-2 px-lg-0 mt-2 mb-3"
          v-model="filter"
        />
        <PaginatedTable
          class="px-2 px-lg-0"
          :fields="fields"
          :perPage="15"
          :searchFilters="filters"
          :pageOptions="[15, 50, 100]"
          :initialSortBy="'lastName'"
          noResultsText="No users "
          tableId="userTable"
          url="/account/GetUsersPage"
          countLabel="Users"
          perPageLabel="Users"
        >
          <template v-slot:cell(phoneNumber)="data">
            <div class="mobile-row-center">{{ data.item.phoneNumber | phoneString }}</div>
          </template>
          <template v-slot:cell(towns)="data">
            <div class="mobile-row-center">
              {{ getTownsText(data.item, 4) }}
              <template v-if="data.item.forestUserTowns.length > 4">
                <b-link @click="showUserTownsModal(data.item)" class="pl-1">
                  and {{ data.item.forestUserTowns.length - 4 }} more...
                </b-link>
              </template>
            </div>
          </template>
          <template v-slot:cell(regionalOfficeId)="data">
            <div class="mobile-row-center">{{ getRegionalOfficeName(data.item) }}</div>
          </template>
          <template v-slot:cell(view)="data">
            <div class="text-lg-right mobile-row-around">
              <b-link @click="showUserModal(data.item)" class="pr-1">View</b-link>
              <template v-if="activeType !== 'public'">
                <span class="d-none d-lg-inline-block">|</span>
                <router-link
                  class="pl-1"
                  tag="a"
                  size="sm"
                  :to="`/userManagement/${data.item.id}/edit`"
                >Edit</router-link>
              </template>
            </div>
          </template>
        </PaginatedTable>
      </b-col>
    </b-row>
    <b-modal
      id="view-user-modal"
      footer-class="text-center"
      centered
      title="User Info"
      hide-header-close
      size="md"
    >
      <ContactDetails :contact="currentUser" :disableRemove="true" :showAll="true" :lineBreak="true">
        <ContactDetailLine v-if="currentUser && currentUser.forestProtectionUnitId"
          :lineBreak="true"
          label="Call Number:"
          :value="currentUser.forestProtectionUnitId"
        />
      </ContactDetails>
      <div slot="modal-footer" class="d-flex full-width">
        <b-button
          pill
          variant="primary"
          @click="$bvModal.hide('view-user-modal')"
        >Ok</b-button>
      </div>
    </b-modal>
    <b-modal
      id="view-user-towns-modal"
      footer-class="text-center"
      centered
      title="Associated Towns"
      hide-header-close
      size="md"
    >
      <div v-if="currentUser && currentUser.towns">
        <div class="overflow-auto p-2 border" style="max-height:250px;">
          <ul class="list-unstyled">
            <li v-for="town in getCurrentUserTowns(currentUser)" :key="town.id">{{town}}</li>
          </ul>
        </div>
      </div>
      <div slot="modal-footer" class="d-flex full-width">
        <b-button
          pill
          variant="primary"
          @click="$bvModal.hide('view-user-towns-modal')"
        >Ok</b-button>
      </div>
    </b-modal>
  </b-container>
</template>

<script>
import { mapState } from 'vuex';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import ContactDetailLine from '@/components/common/ContactDetailLine.vue';
import PaginatedTable from '@/components/common/PaginatedTable.vue';
import ContactDetails from '@/components/account/ContactDetails.vue';
import UserFilter from '@/components/account/UserFilter.vue';

export default {
  name: 'UserManagement',
  components: {
    LoadingSpinner,
    ContactDetails,
    ContactDetailLine,
    PaginatedTable,
    UserFilter,
  },
  data() {
    return {
      filter: '',
      filters: [],
      fields: [],
      activeType: null,
      roleSwitches: [
        { id: 'MFS', label: 'MFS Users', method: this.setMFS },
        { id: 'TCEO', label: 'TCEO Users', method: this.setTCEO },
        { id: 'INFWB', label: 'INFWB Users', method: this.setINFWB },
        { id: 'public', label: 'Public Users', method: this.setPublic },
      ],
    };
  },
  computed: {
    ...mapState(['isExtraSmall']),
    ...mapState('Admin', {
      currentUser: state => state.user,
      loading: state => state.loading,
    }),
  },
  mounted() {
    this.$store.dispatch('Admin/unload');
  },
  watch: {
    isExtraSmall() {
      if (this.isExtraSmall) {
        // move actionField in front of basic fields
        this.shiftMobile();
      } else {
        // move actionField to end of basic fields
        const actionField = this.fields.shift();
        this.fields.push(actionField);
      }
    },
    filter() {
      this.filterChanged();
    },
  },
  methods: {
    shiftMobile() {
      const actionField = this.fields.pop();
      this.fields.unshift(actionField);
    },
    async showUserModal(user) {
      await this.$store.dispatch('Admin/getUser', user.id);
      this.$bvModal.show('view-user-modal');
    },
    async showUserTownsModal(user) {
      await this.$store.dispatch('Admin/getUser', user.id);
      this.$bvModal.show('view-user-towns-modal');
    },
    getUsers() {
      this.$store.dispatch('Admin/getUsers');
    },
    getUser(id) {
      this.$store.dispatch(`Admin/getUser/${id}`);
    },
    setPublic() {
      this.activeType = 'public';
      this.filters = [{ key: 'isPublic', value: true }];
      this.fields = [
        { key: 'firstName', label: 'First Name', sortable: true },
        { key: 'lastName', label: 'Last Name', sortable: true },
        { key: 'forestID', label: 'Forest ID', sortable: true },
        { key: 'phoneNumber', label: 'Phone', sortable: false },
        { key: 'companyName', label: 'Company', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'view', label: '', stickyColumn: true },
      ];

      if (this.isExtraSmall) {
        // move actionField in front of basic fields
        this.shiftMobile();
      }
    },
    setMFS() {
      this.activeType = 'MFS';
      this.filters = [{ key: 'role', value: 'MFS' }];
      this.fields = [
        { key: 'firstName', label: 'First Name', sortable: true },
        { key: 'lastName', label: 'Last Name', sortable: true },
        { key: 'phoneNumber', label: 'Phone', sortable: false },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'forestProtectionUnitId', label: 'Call Number', sortable: true },
        { key: 'view', label: '', stickyColumn: true },
      ];

      if (this.isExtraSmall) {
        // move actionField in front of basic fields
        this.shiftMobile();
      }
    },
    setTCEO() {
      this.activeType = 'TCEO';
      this.filters = [{ key: 'role', value: 'TCEO' }];
      this.fields = [
        { key: 'firstName', label: 'First Name', sortable: true },
        { key: 'lastName', label: 'Last Name', sortable: true },
        { key: 'phoneNumber', label: 'Phone', sortable: false },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'towns', label: 'Towns', sortable: false },
        { key: 'view', label: '', stickyColumn: true },
      ];

      if (this.isExtraSmall) {
        // move actionField in front of basic fields
        this.shiftMobile();
      }
    },
    setINFWB() {
      this.activeType = 'INFWB';
      this.filters = [{ key: 'role', value: 'INFWB' }];
      this.fields = [
        { key: 'firstName', label: 'First Name', sortable: true },
        { key: 'lastName', label: 'Last Name', sortable: true },
        { key: 'phoneNumber', label: 'Phone', sortable: false },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'regionalOfficeId', label: 'Regional Office', sortable: false },
        { key: 'view', label: '', stickyColumn: true },
      ];

      if (this.isExtraSmall) {
        // move actionField in front of basic fields
        this.shiftMobile();
      }
    },
    getTownsText(user, count) {
      return user.forestUserTowns
        .map(fut => fut.town.name)
        .sort()
        .slice(0, count)
        .join(', ');
    },
    getCurrentUserTownsText(user) {
      return this.getCurrentUserTowns(user)
        .join(', ');
    },
    getCurrentUserTowns(user) {
      return user.towns
        .map(t => t.name)
        .sort();
    },
    getRegionalOfficeName(user) {
      return user.regionalOffice ? user.regionalOffice.fullName : '';
    },
    roleSwitchSelected(roleSwitchId) {
      this.roleSwitches.find(rs => rs.id === roleSwitchId).method();
      this.filter = '';
    },
    roleSwitched(role) {
      role.method();
      this.filter = '';
    },
    filterChanged() {
      this.filters = this.filters.filter(f => f.key !== 'filter');

      if (this.filter && this.filter.length > 2) {
        this.filters.push({ key: 'filter', value: this.filter });
      }
    },
  },
  created() {
    this.setMFS();
  },
};
</script>
